/**
 * Analytical Metrics Engine
 *
 * Provides statistical analysis, trend detection, anomaly flagging,
 * and correlation analysis for Bitcoin network metrics.
 *
 * This transforms raw data into actionable intelligence for AI insights.
 */

// ============================================================================
// STATISTICAL UTILITIES
// ============================================================================

/**
 * Calculate mean of an array
 */
const mean = (arr) => {
  if (!arr || arr.length === 0) return null;
  const validValues = arr.filter(v => v != null && !isNaN(v));
  if (validValues.length === 0) return null;
  return validValues.reduce((a, b) => a + b, 0) / validValues.length;
};

/**
 * Calculate standard deviation
 */
const stdDev = (arr) => {
  const avg = mean(arr);
  if (avg === null) return null;
  const validValues = arr.filter(v => v != null && !isNaN(v));
  const squareDiffs = validValues.map(v => Math.pow(v - avg, 2));
  return Math.sqrt(mean(squareDiffs));
};

/**
 * Calculate z-score (how many std devs from mean)
 */
const zScore = (value, arr) => {
  if (value == null) return null;
  const avg = mean(arr);
  const sd = stdDev(arr);
  if (avg === null || sd === null || sd === 0) return null;
  return (value - avg) / sd;
};

/**
 * Calculate percentile rank (0-100)
 */
const percentileRank = (value, arr) => {
  if (value == null || !arr || arr.length === 0) return null;
  const validValues = arr.filter(v => v != null && !isNaN(v)).sort((a, b) => a - b);
  if (validValues.length === 0) return null;
  const belowCount = validValues.filter(v => v < value).length;
  return Math.round((belowCount / validValues.length) * 100);
};

/**
 * Calculate simple moving average from time series
 */
const movingAverage = (values, period) => {
  if (!values || values.length < period) return null;
  const recent = values.slice(-period);
  return mean(recent.map(v => v.y || v));
};

/**
 * Calculate volatility (standard deviation of returns)
 */
const calculateVolatility = (values, period = 30) => {
  if (!values || values.length < period + 1) return null;
  const recent = values.slice(-(period + 1));
  const returns = [];
  for (let i = 1; i < recent.length; i++) {
    const prev = recent[i - 1].y || recent[i - 1];
    const curr = recent[i].y || recent[i];
    if (prev && curr) {
      returns.push((curr - prev) / prev * 100);
    }
  }
  return stdDev(returns);
};

/**
 * Calculate correlation coefficient between two arrays
 */
const correlation = (arr1, arr2) => {
  if (!arr1 || !arr2 || arr1.length !== arr2.length || arr1.length < 3) return null;

  const mean1 = mean(arr1);
  const mean2 = mean(arr2);
  if (mean1 === null || mean2 === null) return null;

  let numerator = 0;
  let denom1 = 0;
  let denom2 = 0;

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] == null || arr2[i] == null) continue;
    const diff1 = arr1[i] - mean1;
    const diff2 = arr2[i] - mean2;
    numerator += diff1 * diff2;
    denom1 += diff1 * diff1;
    denom2 += diff2 * diff2;
  }

  const denominator = Math.sqrt(denom1 * denom2);
  if (denominator === 0) return null;

  return numerator / denominator;
};

/**
 * Detect trend direction and strength
 */
const detectTrend = (values, shortPeriod = 7, longPeriod = 30) => {
  if (!values || values.length < longPeriod) return null;

  const shortMA = movingAverage(values, shortPeriod);
  const longMA = movingAverage(values, longPeriod);
  const current = values[values.length - 1]?.y || values[values.length - 1];

  if (shortMA === null || longMA === null || current == null) return null;

  const shortVsLong = ((shortMA - longMA) / longMA) * 100;
  const currentVsShort = ((current - shortMA) / shortMA) * 100;

  let direction = 'NEUTRAL';
  let strength = 'WEAK';

  if (shortVsLong > 2 && currentVsShort > 0) {
    direction = 'BULLISH';
    strength = shortVsLong > 5 ? 'STRONG' : 'MODERATE';
  } else if (shortVsLong < -2 && currentVsShort < 0) {
    direction = 'BEARISH';
    strength = shortVsLong < -5 ? 'STRONG' : 'MODERATE';
  } else if (Math.abs(shortVsLong) <= 2) {
    direction = 'CONSOLIDATING';
    strength = 'NEUTRAL';
  }

  return {
    direction,
    strength,
    shortMA,
    longMA,
    current,
    shortVsLongPercent: shortVsLong
  };
};

// ============================================================================
// ANOMALY DETECTION
// ============================================================================

/**
 * Detect anomalies based on z-scores
 * Returns array of anomaly objects
 */
const detectAnomalies = (metrics, historicalData) => {
  const anomalies = [];
  const ZSCORE_THRESHOLD = 2.0; // 2 standard deviations

  // Hashrate anomaly (sudden drops are concerning)
  if (metrics.raw?.hashrate && historicalData?.hashrateHistory) {
    const hrZ = zScore(metrics.raw.hashrate, historicalData.hashrateHistory);
    if (hrZ !== null && hrZ < -ZSCORE_THRESHOLD) {
      anomalies.push({
        metric: 'hashrate',
        value: metrics.raw.hashrate,
        zScore: hrZ,
        direction: 'LOW',
        severity: hrZ < -3 ? 'CRITICAL' : 'NOTABLE',
        message: `Hashrate is ${Math.abs(hrZ).toFixed(1)} std devs below recent average - possible miner capitulation`
      });
    }
  }

  // Price volatility anomaly
  if (historicalData?.priceHistory) {
    const volatility = calculateVolatility(historicalData.priceHistory);
    const avgVolatility = historicalData.avgVolatility || 3; // 3% is typical
    if (volatility && volatility > avgVolatility * 2) {
      anomalies.push({
        metric: 'volatility',
        value: volatility,
        direction: 'HIGH',
        severity: volatility > avgVolatility * 3 ? 'CRITICAL' : 'NOTABLE',
        message: `Price volatility is ${(volatility / avgVolatility).toFixed(1)}x higher than normal at ${volatility.toFixed(1)}%`
      });
    }
  }

  // Block time deviation
  if (metrics.raw?.avgBlockTime) {
    const targetBlockTime = 600; // 10 minutes in seconds
    const deviation = ((metrics.raw.avgBlockTime - targetBlockTime) / targetBlockTime) * 100;
    if (Math.abs(deviation) > 10) { // More than 10% off target
      anomalies.push({
        metric: 'avgBlockTime',
        value: metrics.raw.avgBlockTime,
        deviation,
        direction: deviation > 0 ? 'SLOW' : 'FAST',
        severity: Math.abs(deviation) > 20 ? 'CRITICAL' : 'NOTABLE',
        message: deviation > 0
          ? `Blocks are coming ${Math.abs(deviation).toFixed(1)}% SLOWER than target - hashrate may have dropped`
          : `Blocks are coming ${Math.abs(deviation).toFixed(1)}% FASTER than target - hashrate growing rapidly`
      });
    }
  }

  return anomalies;
};

// ============================================================================
// BENCHMARK COMPARISONS
// ============================================================================

const BENCHMARKS = {
  // Hashrate benchmarks (in EH/s)
  hashrate: {
    low: 400e18,      // Below this is unusual
    normal: 700e18,   // Normal operating range
    high: 1000e18,    // Strong hashrate
    units: 'EH/s',
    description: 'Network security indicator'
  },
  // Fee benchmarks (sat/vB)
  fees: {
    low: 5,           // Network uncongested
    normal: 20,       // Healthy fee market
    elevated: 50,     // High demand
    high: 100,        // Significant congestion
    extreme: 500,     // Rare events
    units: 'sat/vB'
  },
  // Mempool benchmarks (tx count)
  mempool: {
    light: 5000,      // Very low activity
    normal: 30000,    // Healthy activity
    busy: 100000,     // High demand
    congested: 150000, // Significant backlog
    units: 'transactions'
  },
  // Block time benchmarks (seconds)
  blockTime: {
    target: 600,      // 10 minutes
    fast: 540,        // 9 minutes
    slow: 660,        // 11 minutes
    units: 'seconds'
  },
  // Price change benchmarks (percent)
  priceChange24h: {
    minor: 2,
    notable: 5,
    significant: 10,
    extreme: 15,
    units: '%'
  }
};

/**
 * Compare a value against benchmarks and return context
 */
const benchmarkComparison = (metric, value, ctx = {}) => {
  if (value == null) return null;
  const benchmark = BENCHMARKS[metric];
  if (!benchmark) return null;

  let level = 'NORMAL';
  let context = '';

  switch (metric) {
    case 'hashrate':
      if (value < benchmark.low) {
        level = 'LOW';
        context = 'Below normal operating range - investigate';
      } else if (value >= benchmark.high) {
        level = 'HIGH';
        context = 'Excellent network security';
      } else {
        level = 'NORMAL';
        context = 'Within normal operating range';
      }
      break;

    case 'fees':
      if (value <= benchmark.low) {
        level = 'LOW';
        context = 'Cheap to transact';
      } else if (value <= benchmark.normal) {
        level = 'NORMAL';
        context = 'Healthy fee market';
      } else if (value <= benchmark.elevated) {
        level = 'ELEVATED';
        context = 'Moderate congestion';
      } else if (value <= benchmark.high) {
        level = 'HIGH';
        context = 'Significant congestion';
      } else {
        level = 'EXTREME';
        context = 'Rare congestion event';
      }
      break;

    case 'mempool':
      if (value < benchmark.light) {
        level = 'LIGHT';
        context = 'Very low activity';
      } else if (value < benchmark.normal) {
        level = 'NORMAL';
        context = 'Healthy activity';
      } else if (value < benchmark.busy) {
        level = 'BUSY';
        context = 'High demand';
      } else if (value < benchmark.congested) {
        level = 'BUSY';
        context = 'Elevated demand, backlog building';
      } else {
        level = 'CONGESTED';
        context = 'Significant backlog';
      }
      // Congestion is fee-driven, not count-driven: a large mempool that still
      // clears at a low fee rate is busy, not congested. Down-rank when fees are cheap.
      if (level === 'CONGESTED' && ctx.fastestFee != null && ctx.fastestFee <= BENCHMARKS.fees.low) {
        level = 'BUSY';
        context = 'High tx count, but fees still low';
      }
      break;

    case 'blockTime':
      const deviation = ((value - benchmark.target) / benchmark.target) * 100;
      if (Math.abs(deviation) <= 5) {
        level = 'ON_TARGET';
        context = 'Normal block production';
      } else if (value < benchmark.fast) {
        level = 'FAST';
        context = 'Hashrate growing faster than difficulty';
      } else if (value > benchmark.slow) {
        level = 'SLOW';
        context = 'Hashrate may have dropped';
      }
      break;

    case 'priceChange24h':
      const absValue = Math.abs(value);
      if (absValue < benchmark.minor) {
        level = 'MINOR';
        context = 'Normal market activity';
      } else if (absValue < benchmark.notable) {
        level = 'NOTABLE';
        context = 'Moderate movement';
      } else if (absValue < benchmark.significant) {
        level = 'SIGNIFICANT';
        context = 'Strong directional move';
      } else {
        level = 'EXTREME';
        context = 'Exceptional volatility';
      }
      break;

    default:
      level = 'UNKNOWN';
      context = 'No benchmark available';
  }

  return { value, level, context, benchmark };
};

// ============================================================================
// CORRELATION ANALYSIS
// ============================================================================

/**
 * Calculate key correlations from historical data
 */
const calculateCorrelations = (historicalData) => {
  if (!historicalData) return null;

  const correlations = {};

  // Price vs Hashrate correlation
  if (historicalData.priceHistory && historicalData.hashrateHistory) {
    const priceValues = historicalData.priceHistory.map(v => v.y || v);
    const hashValues = historicalData.hashrateHistory.map(v => v.y || v);
    const minLen = Math.min(priceValues.length, hashValues.length);
    if (minLen >= 7) {
      correlations.priceHashrate = {
        value: correlation(priceValues.slice(-minLen), hashValues.slice(-minLen)),
        interpretation: null
      };
      if (correlations.priceHashrate.value !== null) {
        const r = correlations.priceHashrate.value;
        if (r > 0.7) correlations.priceHashrate.interpretation = 'Strong positive - hashrate follows price';
        else if (r > 0.3) correlations.priceHashrate.interpretation = 'Moderate positive - some price-hashrate relationship';
        else if (r < -0.3) correlations.priceHashrate.interpretation = 'Negative - hashrate moving opposite to price';
        else correlations.priceHashrate.interpretation = 'Weak - price and hashrate moving independently';
      }
    }
  }

  return correlations;
};

// ============================================================================
// TREND ANALYSIS FOR AI
// ============================================================================

/**
 * Extract time series values from data object
 */
const extractTimeSeries = (data) => {
  if (!data) return null;

  const series = {
    priceHistory: null,
    hashrateHistory: null,
    lightningCapacityHistory: null
  };

  // Price history from ts15 (365 days of daily data)
  if (data?.ts15?.price?.values) {
    series.priceHistory = data.ts15.price.values.slice(-90); // Last 90 days
  }

  // Hashrate history from ts04
  if (data?.ts04?.hashrate?.values) {
    series.hashrateHistory = data.ts04.hashrate.values.slice(-30); // Last 30 days
  }

  // Lightning capacity history from ts06 (30 days)
  if (data?.ts06?.lightning?.length) {
    series.lightningCapacityHistory = data.ts06.lightning
      .slice(-30)
      .map(entry => ({ y: entry.total_capacity }));
  }

  return series;
};

/**
 * Generate comprehensive trend analysis
 */
const generateTrendAnalysis = (data, metrics) => {
  const timeSeries = extractTimeSeries(data);
  if (!timeSeries) return null;

  const analysis = {
    price: null,
    hashrate: null,
    lightning: null,
    movingAverages: {}
  };

  // Price trend analysis
  if (timeSeries.priceHistory && timeSeries.priceHistory.length >= 30) {
    analysis.price = detectTrend(timeSeries.priceHistory, 7, 30);

    // Calculate multiple moving averages
    const prices = timeSeries.priceHistory;
    analysis.movingAverages.price = {
      ma7: movingAverage(prices, 7),
      ma30: movingAverage(prices, 30),
      ma90: prices.length >= 90 ? movingAverage(prices, 90) : null
    };

    // Add price position relative to MAs
    const currentPrice = metrics?.raw?.price;
    if (currentPrice && analysis.movingAverages.price.ma7) {
      analysis.movingAverages.price.currentVsMa7 =
        ((currentPrice - analysis.movingAverages.price.ma7) / analysis.movingAverages.price.ma7) * 100;
    }
    if (currentPrice && analysis.movingAverages.price.ma30) {
      analysis.movingAverages.price.currentVsMa30 =
        ((currentPrice - analysis.movingAverages.price.ma30) / analysis.movingAverages.price.ma30) * 100;
    }

    // Calculate volatility
    analysis.priceVolatility = calculateVolatility(prices, 30);

    // Percentile rank (where is current price in 90-day range?)
    analysis.pricePercentile = percentileRank(
      currentPrice,
      prices.map(v => v.y || v)
    );
  }

  // Hashrate trend analysis
  if (timeSeries.hashrateHistory && timeSeries.hashrateHistory.length >= 14) {
    analysis.hashrate = detectTrend(timeSeries.hashrateHistory, 7, 14);

    const hashrates = timeSeries.hashrateHistory;
    analysis.movingAverages.hashrate = {
      ma7: movingAverage(hashrates, 7),
      ma14: movingAverage(hashrates, 14)
    };
  }

  // Lightning capacity trend analysis
  if (timeSeries.lightningCapacityHistory && timeSeries.lightningCapacityHistory.length >= 14) {
    analysis.lightning = detectTrend(timeSeries.lightningCapacityHistory, 7, 14);

    analysis.movingAverages.lightning = {
      ma7: movingAverage(timeSeries.lightningCapacityHistory, 7),
      ma14: movingAverage(timeSeries.lightningCapacityHistory, 14)
    };
  }

  return analysis;
};

// ============================================================================
// MAIN EXPORT: COMPUTE ANALYTICAL METRICS
// ============================================================================

/**
 * Compute comprehensive analytical metrics from canonical metrics and raw data
 *
 * @param {Object} canonicalMetrics - Output from calculateCanonicalMetrics()
 * @param {Object} data - Raw API data with time series
 * @returns {Object} Enhanced metrics with statistical analysis
 */
export const computeAnalyticalMetrics = (canonicalMetrics, data) => {
  if (!canonicalMetrics) return null;

  // Extract historical data for analysis
  const historicalData = extractTimeSeries(data);

  // Generate trend analysis
  const trends = generateTrendAnalysis(data, canonicalMetrics);

  // Detect anomalies
  const anomalies = detectAnomalies(canonicalMetrics, historicalData);

  // Calculate correlations
  const correlations = calculateCorrelations(historicalData);

  // Benchmark comparisons for key metrics
  const benchmarks = {
    hashrate: benchmarkComparison('hashrate', canonicalMetrics.raw?.hashrate),
    fees: benchmarkComparison('fees', canonicalMetrics.raw?.fastestFee),
    mempool: benchmarkComparison('mempool', canonicalMetrics.raw?.mempoolSize, { fastestFee: canonicalMetrics.raw?.fastestFee }),
    blockTime: benchmarkComparison('blockTime', canonicalMetrics.raw?.avgBlockTime),
    priceChange: benchmarkComparison('priceChange24h', canonicalMetrics.raw?.priceChange24h)
  };

  // Market condition summary
  const marketCondition = determineMarketCondition(canonicalMetrics, trends, anomalies);

  return {
    // Original canonical metrics
    ...canonicalMetrics,

    // Enhanced analytics
    analytics: {
      trends,
      anomalies,
      correlations,
      benchmarks,
      marketCondition,

      // Summary flags for AI
      flags: {
        hasAnomalies: anomalies.length > 0,
        anomalyCount: anomalies.length,
        criticalAnomalies: anomalies.filter(a => a.severity === 'CRITICAL').length,
        trendDirection: trends?.price?.direction || 'UNKNOWN',
        volatilityLevel: categorizeVolatility(trends?.priceVolatility),
        networkHealth: determineNetworkHealth(canonicalMetrics, benchmarks)
      }
    }
  };
};

/**
 * Determine overall market condition
 */
const determineMarketCondition = (metrics, trends, anomalies) => {
  const conditions = [];
  let sentiment = 'NEUTRAL';
  let confidence = 'MEDIUM';

  // Price trend contribution
  if (trends?.price?.direction === 'BULLISH') {
    conditions.push('Upward price trend');
    sentiment = 'BULLISH';
  } else if (trends?.price?.direction === 'BEARISH') {
    conditions.push('Downward price trend');
    sentiment = 'BEARISH';
  }

  // ATH proximity
  const athChange = metrics?.raw?.athChangePercent;
  if (athChange != null) {
    if (athChange > -5) {
      conditions.push('Near all-time highs');
      if (sentiment !== 'BEARISH') sentiment = 'BULLISH';
    } else if (athChange < -30) {
      conditions.push('Significant correction from ATH');
    }
  }

  // Critical anomalies override
  const criticalCount = anomalies.filter(a => a.severity === 'CRITICAL').length;
  if (criticalCount > 0) {
    conditions.push(`${criticalCount} critical anomaly detected`);
    confidence = 'HIGH';
  }

  // Network health
  const hashrateTrend = trends?.hashrate?.direction;
  if (hashrateTrend === 'BULLISH') {
    conditions.push('Growing hashrate');
  } else if (hashrateTrend === 'BEARISH') {
    conditions.push('Declining hashrate');
  }

  return {
    sentiment,
    confidence,
    conditions,
    summary: conditions.length > 0 ? conditions.join('; ') : 'Normal market conditions'
  };
};

/**
 * Categorize volatility level
 */
const categorizeVolatility = (volatility) => {
  if (volatility == null) return 'UNKNOWN';
  if (volatility < 2) return 'LOW';
  if (volatility < 4) return 'NORMAL';
  if (volatility < 7) return 'ELEVATED';
  return 'HIGH';
};

/**
 * Determine network health status
 */
const determineNetworkHealth = (metrics, benchmarks) => {
  const issues = [];

  if (benchmarks?.hashrate?.level === 'LOW') {
    issues.push('Low hashrate');
  }
  if (benchmarks?.blockTime?.level === 'SLOW') {
    issues.push('Slow block times');
  }
  if (benchmarks?.mempool?.level === 'CONGESTED') {
    issues.push('Mempool congestion');
  }

  if (issues.length === 0) return 'HEALTHY';
  if (issues.length === 1) return 'MINOR_ISSUES';
  return 'STRESSED';
};

// ============================================================================
// FORMAT FOR AI PROMPT
// ============================================================================

/**
 * Format analytical metrics for AI consumption
 * Returns enhanced context string to prepend to metrics
 */
export const formatAnalyticsForAI = (analytics) => {
  if (!analytics) return '';

  const lines = [];

  lines.push('=== MARKET CONDITION ASSESSMENT ===');
  if (analytics.marketCondition) {
    lines.push(`Overall Sentiment: ${analytics.marketCondition.sentiment}`);
    lines.push(`Confidence: ${analytics.marketCondition.confidence}`);
    lines.push(`Summary: ${analytics.marketCondition.summary}`);
  }

  // Trend analysis
  if (analytics.trends?.price) {
    lines.push('');
    lines.push('=== PRICE TREND ANALYSIS ===');
    lines.push(`Direction: ${analytics.trends.price.direction} (${analytics.trends.price.strength})`);
    if (analytics.trends.movingAverages?.price) {
      const ma = analytics.trends.movingAverages.price;
      if (ma.ma7) lines.push(`7-Day MA: $${ma.ma7.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
      if (ma.ma30) lines.push(`30-Day MA: $${ma.ma30.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
      if (ma.currentVsMa7 != null) lines.push(`Current vs 7d MA: ${ma.currentVsMa7 > 0 ? '+' : ''}${ma.currentVsMa7.toFixed(2)}%`);
      if (ma.currentVsMa30 != null) lines.push(`Current vs 30d MA: ${ma.currentVsMa30 > 0 ? '+' : ''}${ma.currentVsMa30.toFixed(2)}%`);
    }
    if (analytics.priceVolatility != null) {
      lines.push(`30-Day Volatility: ${analytics.priceVolatility.toFixed(2)}% (${analytics.flags.volatilityLevel})`);
    }
    if (analytics.pricePercentile != null) {
      lines.push(`90-Day Percentile: ${analytics.pricePercentile}th percentile`);
    }
  }

  // Lightning trend
  if (analytics.trends?.lightning) {
    lines.push('');
    lines.push('=== LIGHTNING CAPACITY TREND ===');
    lines.push(`Direction: ${analytics.trends.lightning.direction} (${analytics.trends.lightning.strength})`);
    if (analytics.trends.movingAverages?.lightning) {
      const lnMa = analytics.trends.movingAverages.lightning;
      if (lnMa.ma7) lines.push(`7-Day MA: ${(lnMa.ma7 / 1e8).toFixed(0)} BTC`);
      if (lnMa.ma14) lines.push(`14-Day MA: ${(lnMa.ma14 / 1e8).toFixed(0)} BTC`);
    }
  }

  // Anomalies (critical first)
  if (analytics.anomalies && analytics.anomalies.length > 0) {
    lines.push('');
    lines.push('=== ANOMALIES DETECTED ===');
    analytics.anomalies
      .sort((a, b) => (a.severity === 'CRITICAL' ? -1 : 1))
      .forEach(a => {
        lines.push(`[${a.severity}] ${a.message}`);
      });
  }

  // Benchmark context
  if (analytics.benchmarks) {
    lines.push('');
    lines.push('=== BENCHMARK COMPARISONS ===');
    Object.entries(analytics.benchmarks).forEach(([key, b]) => {
      if (b) {
        lines.push(`${key}: ${b.level} - ${b.context}`);
      }
    });
  }

  // Correlations
  if (analytics.correlations) {
    lines.push('');
    lines.push('=== CORRELATIONS ===');
    if (analytics.correlations.priceHashrate?.value != null) {
      lines.push(`Price-Hashrate: ${analytics.correlations.priceHashrate.value.toFixed(2)} (${analytics.correlations.priceHashrate.interpretation})`);
    }
    if (analytics.correlations.feeMempool?.value != null) {
      lines.push(`Fee-Mempool: ${analytics.correlations.feeMempool.value.toFixed(2)} (${analytics.correlations.feeMempool.interpretation})`);
    }
  }

  // Network health
  lines.push('');
  lines.push(`=== NETWORK STATUS: ${analytics.flags.networkHealth} ===`);

  return lines.join('\n');
};

export default computeAnalyticalMetrics;
